import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PartnerSearchByBrResponse, ServiceAgreementV2 } from 'pco-product-service-internal-consumer';
import { Active, InOpening } from 'types';
import { useGetServiceAgreementDetails } from '../../../../../hooks/queries/privateBanking';
import { useErrorModal } from '../../../../../hooks/useErrorModal';
import { filterVisibleServiceRoles } from '../../../../../utils/serviceRoleStatusUtils';
import { getPartnerUidChanges, getUniquePartnerUids, haveSamePartnerUids } from '../utils/partnerSelectionUtils';
import { buildPartnerFromServiceRole, PrivateBankingServiceRole } from '../utils/serviceRoleMapper';
import type { SaveBlocker, SaveState } from '../../utils/saveGate';
import { useSaveServiceRolesAndNotify } from '../../hooks/useSaveServiceRolesAndNotify';

const DISTRIBUTED_EDIT_STATUSES = new Set<string>([
  InOpening.READY_FOR_DISTRIBUTION,
  Active.ACTIVE,
  'IN_DISTRIBUTION',
  'IN DISTRIBUTION',
]);

// ACTIVE is intentionally excluded: removing the last active partner is the product-closure path.
// Keep the ConfigureProductModal regression test for "last active partner removal" green if this set changes.
const PARTNER_REQUIRED_STATUSES = new Set<string>([
  InOpening.READY_FOR_DISTRIBUTION,
  'IN_DISTRIBUTION',
  'IN DISTRIBUTION',
]);

type PartnerSelectionDraft = {
  key: string;
  partners: PartnerSearchByBrResponse[];
};

type Props = {
  serviceAgreement: ServiceAgreementV2;
  contactNoteUids?: string[];
  onSaveStateChange: (state: SaveState) => void;
};

type UsePartnerSelectionSectionResult = {
  isServiceAgreementDetailsFetching: boolean;
  isPartnerSelectionDisabled: boolean;
  selectedPartners: PartnerSearchByBrResponse[];
  setSelectedPartners: (partners: PartnerSearchByBrResponse[]) => void;
  showProductClosureWarning: boolean;
};

export const usePartnerSelectionSection = ({
  serviceAgreement,
  contactNoteUids,
  onSaveStateChange,
}: Props): UsePartnerSelectionSectionResult => {
  const { t } = useTranslation();
  const { showErrorModal } = useErrorModal();
  const serviceAgreementUid = serviceAgreement.serviceAgreementId;
  const { isPending, saveAndNotify, showSaveErrorModal } = useSaveServiceRolesAndNotify();
  const [selectedPartnersDraft, setSelectedPartnersDraft] = useState<PartnerSelectionDraft | null>(null);

  const shouldLoadCurrentServiceRoles =
    !!serviceAgreementUid && DISTRIBUTED_EDIT_STATUSES.has(serviceAgreement.agreementStatus ?? '');

  const partnerSelectionKey = `${serviceAgreementUid ?? 'without-service-agreement'}:${
    shouldLoadCurrentServiceRoles ? 'current-service-roles' : 'new-service-roles'
  }`;

  const {
    data: serviceAgreementDetails,
    isFetching: isServiceAgreementDetailsFetching,
    isError: isServiceAgreementDetailsError,
    error: serviceAgreementDetailsError,
  } = useGetServiceAgreementDetails(shouldLoadCurrentServiceRoles ? serviceAgreementUid : undefined);

  const initialSelectedPartners = useMemo(() => {
    if (!shouldLoadCurrentServiceRoles || !serviceAgreementDetails) return [];
    return filterVisibleServiceRoles((serviceAgreementDetails.serviceRoles ?? []) as PrivateBankingServiceRole[])
      .filter((role) => role.status !== 'INACTIVE')
      .map(buildPartnerFromServiceRole);
  }, [serviceAgreementDetails, shouldLoadCurrentServiceRoles]);

  const selectedPartners =
    selectedPartnersDraft?.key === partnerSelectionKey ? selectedPartnersDraft.partners : initialSelectedPartners;

  const setSelectedPartners = useCallback(
    (partners: PartnerSearchByBrResponse[]) => {
      setSelectedPartnersDraft({ key: partnerSelectionKey, partners });
    },
    [partnerSelectionKey],
  );

  const initialPartnerUids = useMemo(() => getUniquePartnerUids(initialSelectedPartners), [initialSelectedPartners]);
  const selectedPartnerUids = useMemo(() => getUniquePartnerUids(selectedPartners), [selectedPartners]);
  const isPartnerSelectionChanged = !haveSamePartnerUids(initialPartnerUids, selectedPartnerUids);
  const isPartnerSelectionDisabled = isPending || isServiceAgreementDetailsFetching || isServiceAgreementDetailsError;
  const isPartnerRequired = PARTNER_REQUIRED_STATUSES.has(serviceAgreement.agreementStatus ?? '');
  const hasRequiredPartnerSelection = !isPartnerRequired || selectedPartners.length > 0;
  const requiresProductClosureConfirmation =
    serviceAgreement.agreementStatus === Active.ACTIVE &&
    shouldLoadCurrentServiceRoles &&
    initialSelectedPartners.length > 0 &&
    selectedPartners.length === 0 &&
    isPartnerSelectionChanged;
  const showProductClosureWarning =
    requiresProductClosureConfirmation &&
    !isPending &&
    !isServiceAgreementDetailsFetching &&
    !isServiceAgreementDetailsError;

  const canSave =
    isPartnerSelectionChanged &&
    hasRequiredPartnerSelection &&
    !isServiceAgreementDetailsFetching &&
    !isServiceAgreementDetailsError;

  const showEmptySelectionTooltip =
    selectedPartners.length === 0 &&
    (isPartnerRequired || !shouldLoadCurrentServiceRoles) &&
    !isPending &&
    !isServiceAgreementDetailsFetching &&
    !isServiceAgreementDetailsError;

  const showUnchangedSelectionTooltip =
    shouldLoadCurrentServiceRoles &&
    !isPartnerSelectionChanged &&
    !isPending &&
    !isServiceAgreementDetailsFetching &&
    !isServiceAgreementDetailsError;

  const tooltip = showUnchangedSelectionTooltip
    ? t('pb.configureproductmodal.save.unchanged.tooltip')
    : showEmptySelectionTooltip
      ? t('pb.configureproductmodal.save.nopartner.tooltip')
      : null;

  const { addedPartnerUids, removedPartnerUids } = useMemo(
    () => getPartnerUidChanges(initialPartnerUids, selectedPartnerUids),
    [initialPartnerUids, selectedPartnerUids],
  );

  useEffect(() => {
    if (!isServiceAgreementDetailsError) return;
    showErrorModal(serviceAgreementDetailsError, {
      title: 'Error when loading service roles',
      userErrorMessage: 'An error occurred when loading service roles. Please try again later.',
    });
  }, [isServiceAgreementDetailsError, serviceAgreementDetailsError, showErrorModal]);

  const handleSave = useCallback(async () => {
    if (!canSave || isPending) return;

    if (!serviceAgreementUid) {
      await showSaveErrorModal(new Error('serviceAgreementId is required'));
      return;
    }

    if (selectedPartnerUids.length !== selectedPartners.length) {
      await showSaveErrorModal(new Error('Selected partners must have a technical UUID'));
      return;
    }

    await saveAndNotify({
      contactNoteUids,
      serviceAgreementUid,
      partnerIds: selectedPartnerUids,
      successToastTitle:
        addedPartnerUids.length > 0 && removedPartnerUids.length > 0
          ? 'Partner(s) is successfully added and removed'
          : addedPartnerUids.length > 0
            ? 'Partner(s) is successfully added'
            : 'Partner(s) is successfully removed',
    });
  }, [
    canSave,
    isPending,
    serviceAgreementUid,
    selectedPartnerUids,
    selectedPartners.length,
    addedPartnerUids.length,
    removedPartnerUids.length,
    contactNoteUids,
    saveAndNotify,
    showSaveErrorModal,
  ]);

  const blocker = useMemo<SaveBlocker>(() => ({ blocked: !canSave, reason: tooltip }), [canSave, tooltip]);

  useEffect(() => {
    onSaveStateChange({
      blocker,
      isPending,
      save: handleSave,
      addsServiceRoles: addedPartnerUids.length > 0,
      requiresProductClosureConfirmation,
    });
  }, [blocker, isPending, handleSave, requiresProductClosureConfirmation, addedPartnerUids.length, onSaveStateChange]);

  return {
    isServiceAgreementDetailsFetching,
    isPartnerSelectionDisabled,
    selectedPartners,
    setSelectedPartners,
    showProductClosureWarning,
  };
};
